import React from "react";
import {
  Box,
  Flex,
  Text,
  useColorModeValue,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Button,
  Badge,
} from "@chakra-ui/react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "react-query";
import { getGateway, getGatewayDigikalaOrders } from "../services/gatewayService";
import Card from "../components/Card/Card";
import CardHeader from "../components/Card/CardHeader";
import CardBody from "../components/Card/CardBody";
import { formatDateShamsiShort, toDate } from "../utils/date";

function formatDate(value) {
  if (!value) return "—";
  const date = toDate(value);
  if (!date) return String(value);
  return formatDateShamsiShort(date);
}

function DigikalaOrders() {
  const { id } = useParams();
  const navigate = useNavigate();
  const textColor = useColorModeValue("gray.700", "white");
  const borderColor = useColorModeValue("gray.200", "gray.600");

  const {
    data: gatewayRes,
    isLoading: gatewayLoading,
    error: gatewayError,
  } = useQuery(["gateway", id], () => getGateway(id), {
    enabled: !!id,
  });

  const {
    data: ordersRes,
    isLoading: ordersLoading,
    error: ordersError,
    refetch,
  } = useQuery(["gateway-digikala-orders", id], () => getGatewayDigikalaOrders(id), {
    enabled: !!id,
  });

  const isLoading = gatewayLoading || ordersLoading;
  const gateway = gatewayRes?.data;
  const ordersData = ordersRes?.data || {};
  const items = ordersData.items || [];
  const pager = ordersData.pager || {};

  if (gatewayError) {
    return (
      <Flex direction="column" dir="rtl">
        <Box mx="auto" p={{ base: "20px", md: "30px" }} pe="20px" minH="100vh">
          <Card>
            <CardBody>
              <Alert status="error" borderRadius="md">
                <AlertIcon />
                <AlertTitle>درگاه یافت نشد</AlertTitle>
                <AlertDescription>
                  {gatewayError.response?.data?.message || gatewayError.message}
                </AlertDescription>
              </Alert>
              <Button mt={4} colorScheme="teal" onClick={() => navigate("/gateways")}>
                بازگشت به لیست درگاه‌ها
              </Button>
            </CardBody>
          </Card>
        </Box>
      </Flex>
    );
  }

  if (isLoading || !gateway) {
    return (
      <Flex direction="column" dir="rtl">
        <Box mx="auto" p={{ base: "20px", md: "30px" }} pe="20px" minH="100vh">
          <Card>
            <CardBody>
              <Flex justify="center" align="center" minH="400px">
                <Spinner size="xl" color="teal.500" />
                <Text mr={4} fontSize="lg" color={textColor}>
                  در حال بارگذاری سفارشات دیجی‌کالا...
                </Text>
              </Flex>
            </CardBody>
          </Card>
        </Box>
      </Flex>
    );
  }

  const typeLabel =
    gateway.type === "digikala"
      ? "دیجی‌کالا"
      : gateway.type === "basalam"
      ? "باسلام"
      : "سایت شخصی";

  return (
    <Flex direction="column" dir="rtl">
      <Box mx="auto" p={{ base: "20px", md: "30px" }} pe="20px" minH="100vh">
        <Button
          variant="ghost"
          size="sm"
          mb={4}
          onClick={() => navigate(`/gateways/${id}`)}
        >
          ← بازگشت به پروفایل درگاه
        </Button>

        <Card mb={6}>
          <CardHeader>
            <Flex justify="space-between" align="center" flexWrap="wrap" gap={2}>
              <Box>
                <Text fontSize="lg" fontWeight="bold" color={textColor}>
                  سفارشات دیجی‌کالا - {gateway.name}
                </Text>
                <Text fontSize="sm" color="gray.400" mt={1}>
                  لیست سفارشات فعال این درگاه از API دیجی‌کالا (page 1)
                </Text>
              </Box>
              <Badge
                bg={
                  gateway.type === "digikala"
                    ? "#ef4056"
                    : gateway.type === "basalam"
                    ? "#ff5c35"
                    : undefined
                }
                colorScheme={
                  gateway.type !== "digikala" && gateway.type !== "basalam"
                    ? "gray"
                    : undefined
                }
                color={
                  gateway.type === "digikala" || gateway.type === "basalam"
                    ? "white"
                    : undefined
                }
                fontSize="md"
                p="4px 12px"
                borderRadius="8px"
              >
                {typeLabel}
              </Badge>
            </Flex>
          </CardHeader>
        </Card>

        {ordersError && (
          <Card mb={6}>
            <CardBody>
              <Alert status="error" borderRadius="md">
                <AlertIcon />
                <AlertTitle>خطا در دریافت سفارشات</AlertTitle>
                <AlertDescription>
                  {ordersError.response?.data?.message || ordersError.message}
                  {ordersError.response?.data?.errors && (
                    <Box mt={2}>
                      <Text fontSize="sm" fontWeight="bold">جزئیات خطا:</Text>
                      <pre style={{ fontSize: '12px', marginTop: '8px', whiteSpace: 'pre-wrap' }}>
                        {JSON.stringify(ordersError.response.data.errors, null, 2)}
                      </pre>
                    </Box>
                  )}
                  {ordersError.response?.data?.raw && (
                    <Box mt={2}>
                      <Text fontSize="sm" fontWeight="bold">پاسخ خام API:</Text>
                      <pre style={{ fontSize: '12px', marginTop: '8px', whiteSpace: 'pre-wrap', maxHeight: '200px', overflow: 'auto' }}>
                        {JSON.stringify(ordersError.response.data.raw, null, 2)}
                      </pre>
                    </Box>
                  )}
                </AlertDescription>
              </Alert>
              <Button
                mt={4}
                colorScheme="teal"
                onClick={() => refetch()}
                isLoading={ordersLoading}
              >
                تلاش مجدد
              </Button>
            </CardBody>
          </Card>
        )}

        {/* آمار کلی */}
        {ordersData.stats && (
          <Card mb={4}>
            <CardBody>
              <Text fontSize="md" fontWeight="bold" mb={4} color={textColor}>
                آمار کلی سفارشات
              </Text>
              <Flex flexWrap="wrap" gap={4} justify="space-between">
                <Box textAlign="center" minW="140px" p={3} bg="white" borderRadius="md" border="1px solid" borderColor="gray.200">
                  <Text fontSize="xs" color="gray.500" mb={1}>تعداد کل سفارشات</Text>
                  <Text fontSize="lg" fontWeight="bold" color="teal.500">
                    {ordersData.stats.totalOrders}
                  </Text>
                </Box>
                <Box textAlign="center" minW="140px" p={3} bg="white" borderRadius="md" border="1px solid" borderColor="gray.200">
                  <Text fontSize="xs" color="gray.500" mb={1}>مبلغ کل</Text>
                  <Text fontSize="lg" fontWeight="bold" color="green.500">
                    {ordersData.stats.totalAmountFormatted}
                  </Text>
                </Box>
                <Box textAlign="center" minW="140px" p={3} bg="white" borderRadius="md" border="1px solid" borderColor="gray.200">
                  <Text fontSize="xs" color="gray.500" mb={1}>تعداد کل اقلام</Text>
                  <Text fontSize="lg" fontWeight="bold" color="blue.500">
                    {ordersData.stats.totalQuantity}
                  </Text>
                </Box>
                <Box textAlign="center" minW="140px" p={3} bg="white" borderRadius="md" border="1px solid" borderColor="gray.200">
                  <Text fontSize="xs" color="gray.500" mb={1}>میانگین ارزش سفارش</Text>
                  <Text fontSize="lg" fontWeight="bold" color="purple.500">
                    {ordersData.stats.averageOrderValueFormatted}
                  </Text>
                </Box>
                <Box textAlign="center" minW="140px" p={3} bg="white" borderRadius="md" border="1px solid" borderColor="gray.200">
                  <Text fontSize="xs" color="gray.500" mb={1}>پردازش شده</Text>
                  <Text fontSize="lg" fontWeight="bold" color="green.500">
                    {ordersData.stats.processedCount}
                  </Text>
                </Box>
                <Box textAlign="center" minW="140px" p={3} bg="white" borderRadius="md" border="1px solid" borderColor="gray.200">
                  <Text fontSize="xs" color="gray.500" mb={1}>در انتظار</Text>
                  <Text fontSize="lg" fontWeight="bold" color="orange.500">
                    {ordersData.stats.pendingCount}
                  </Text>
                </Box>
              </Flex>
            </CardBody>
          </Card>
        )}

        <Card>
          <CardBody>
            <Box mb={4}>
              <Text fontSize="sm" color="gray.500">
                تعداد کل سفارشات:{" "}
                <Text as="span" fontWeight="bold">
                  {pager.totalItems ?? pager.total_rows ?? "—"}
                </Text>
                {"  "} (صفحه {pager.currentPage ?? pager.page ?? 1} از {pager.totalPages ?? pager.total_pages ?? "—"})
              </Text>
              {ordersData.rate_limit && (
                <Text fontSize="xs" color="gray.400" mt={1}>
                  Rate Limit: {ordersData.rate_limit.current}/{ordersData.rate_limit.max} درخواست
                </Text>
              )}
            </Box>
            <Box overflowX="auto" className="table-scroll">
              <Table variant="simple" color={textColor} size="sm">
                <Thead>
                  <Tr>
                    <Th borderColor={borderColor}>شناسه</Th>
                    <Th borderColor={borderColor}>عنوان محصول</Th>
                    <Th borderColor={borderColor}>کد فروشنده</Th>
                    <Th borderColor={borderColor}>تعداد</Th>
                    <Th borderColor={borderColor}>قیمت فروش</Th>
                    <Th borderColor={borderColor}>تخفیف</Th>
                    <Th borderColor={borderColor}>قیمت نهایی</Th>
                    <Th borderColor={borderColor}>تاریخ سفارش</Th>
                    <Th borderColor={borderColor}>وضعیت انبار</Th>
                    <Th borderColor={borderColor}>زمان پردازش</Th>
                    <Th borderColor={borderColor}>تحویل</Th>
                    <Th borderColor={borderColor}>وضعیت</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {items.length === 0 ? (
                    <Tr>
                      <Td colSpan={12} textAlign="center" py={8}>
                        <Text color="gray.400">سفارشی برای نمایش وجود ندارد.</Text>
                      </Td>
                    </Tr>
                  ) : (
                    items.map((item, idx) => (
                      <Tr key={item.id || `${item.orderId}-${item.orderItemId}-${idx}`}>
                        <Td borderColor={borderColor}>
                          <Text fontSize="xs" color="gray.500">
                            {item.orderId}
                          </Text>
                        </Td>
                        <Td borderColor={borderColor} maxW="200px">
                          <Text fontSize="sm" fontWeight="bold" color="teal.500" noOfLines={2}>
                            {item.productTitle || "—"}
                          </Text>
                          <Text fontSize="xs" color="gray.400" mt={1}>
                            ID: {item.productVariantId}
                          </Text>
                        </Td>
                        <Td borderColor={borderColor}>
                          <Text fontSize="sm" fontFamily="mono">
                            {item.supplierCode || "—"}
                          </Text>
                        </Td>
                        <Td borderColor={borderColor}>
                          <Badge colorScheme="blue" fontSize="sm">
                            {item.quantity || "—"}
                          </Badge>
                        </Td>
                        <Td borderColor={borderColor}>
                          <Text fontSize="sm" fontWeight="bold">
                            {item.sellingPriceFormatted || "—"}
                          </Text>
                        </Td>
                        <Td borderColor={borderColor}>
                          <Text fontSize="xs" color="red.500">
                            {(item.amazingDiscount || 0) + (item.discountManager || 0) > 0 
                              ? `${((item.amazingDiscount || 0) + (item.discountManager || 0)).toLocaleString('fa-IR')} تومان`
                              : "—"}
                          </Text>
                        </Td>
                        <Td borderColor={borderColor}>
                          <Text fontSize="sm" fontWeight="bold" color="green.600">
                            {item.totalPriceFormatted || "—"}
                          </Text>
                        </Td>
                        <Td borderColor={borderColor}>
                          <Text fontSize="xs">
                            {item.orderCreatedAtFormatted || formatDate(item.orderCreatedAt)}
                          </Text>
                        </Td>
                        <Td borderColor={borderColor}>
                          <Badge 
                            colorScheme={item.warehouseStatus === 'پردازش شده' ? 'green' : 'orange'}
                            fontSize="xs"
                          >
                            {item.warehouseStatus || "نامشخص"}
                          </Badge>
                          {item.warehouseStatusAtFormatted && (
                            <Text fontSize="xs" color="gray.400" mt={1}>
                              {item.warehouseStatusAtFormatted}
                            </Text>
                          )}
                        </Td>
                        <Td borderColor={borderColor}>
                          <Text fontSize="xs" color="blue.500">
                            {item.processingTimeFormatted || "—"}
                          </Text>
                        </Td>
                        <Td borderColor={borderColor}>
                          <Text fontSize="xs">
                            {item.deliveryTimeRemainingFormatted || "—"}
                          </Text>
                          {item.commitmentDateFormatted && (
                            <Text fontSize="xs" color="gray.400" mt={1}>
                              {item.commitmentDateFormatted}
                            </Text>
                          )}
                        </Td>
                        <Td borderColor={borderColor}>
                          <Badge 
                            colorScheme={item.cancelable ? 'red' : 'gray'}
                            fontSize="xs"
                          >
                            {item.cancelableText || (item.cancelable ? 'قابل لغو' : 'غیرقابل لغو')}
                          </Badge>
                        </Td>
                      </Tr>
                    ))
                  )}
                </Tbody>
              </Table>
            </Box>
          </CardBody>
        </Card>
      </Box>
    </Flex>
  );
}

export default DigikalaOrders;

